import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ShoppingBag, ChevronLeft, ChevronRight, Expand, X } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, priceLabel } from "../lib/api";
import { useCart } from "../context/CartContext";
import { FadeUp, Overline } from "../components/Reveal";

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [size, setSize] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    setProduct(null);
    setSize(null);
    setActiveImg(0);
    api
      .get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  const images = product?.images || [];
  const n = images.length;

  const go = useCallback(
    (dir) => {
      if (n < 2) return;
      setActiveImg((i) => (i + dir + n) % n);
    },
    [n]
  );

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, go]);

  const handleAdd = () => {
    if (!size) {
      toast.error("Select a size first");
      return;
    }
    addItem(product, size);
    toast.success(`${product.name} added to your selection`);
  };

  if (notFound) {
    return (
      <div className="min-h-screen pt-40 pb-28 px-6 text-center" data-testid="product-not-found">
        <p className="font-serif text-4xl text-zinc-400 italic">This piece has been claimed</p>
        <Link to="/shop" className="mt-6 inline-block text-xs tracking-[0.3em] uppercase text-gold">
          Return to Collections
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-40 pb-28 px-6 md:px-12 max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="aspect-[4/5] bg-[#141414] animate-pulse" />
          <div className="space-y-4">
            <div className="h-8 w-1/2 bg-[#141414] animate-pulse" />
            <div className="h-4 w-1/3 bg-[#141414] animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="product-detail-page" className="min-h-screen pt-28 md:pt-36 pb-28">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <Link
          to="/shop"
          data-testid="product-back-link"
          className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-zinc-500 hover:text-gold transition-colors duration-300"
        >
          <ArrowLeft size={13} /> Collections
        </Link>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
          <div className="md:sticky md:top-32 self-start">
            {/* Main preview */}
            <div className="relative group aspect-[4/5] overflow-hidden border border-white/10 bg-[#0B0B0B]">
              {images[activeImg] && (
                <img
                  src={fileUrl(images[activeImg])}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-30 saturate-150"
                />
              )}
              <div className="pointer-events-none absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_50%_35%,rgba(212,175,55,0.08),transparent_60%)]" />
              <AnimatePresence mode="wait">
                <motion.img
                  key={activeImg}
                  data-testid="product-image-main"
                  src={images[activeImg] ? fileUrl(images[activeImg]) : ""}
                  alt={product.name}
                  onClick={() => n > 0 && setLightbox(true)}
                  initial={{ opacity: 0, scale: 1.01 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-0 w-full h-full object-contain p-3 cursor-zoom-in"
                />
              </AnimatePresence>
              {!images[activeImg] && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-serif text-8xl text-zinc-700 italic">V</span>
                </div>
              )}

              {n > 0 && (
                <button
                  data-testid="product-expand"
                  onClick={() => setLightbox(true)}
                  className="absolute top-4 right-4 p-2.5 bg-black/50 backdrop-blur-sm text-zinc-300 border border-white/10 opacity-0 group-hover:opacity-100 hover:text-gold hover:border-gold/40 transition-all duration-300"
                  aria-label="View full image"
                >
                  <Expand size={15} strokeWidth={1.5} />
                </button>
              )}

              {n > 1 && (
                <>
                  <button
                    data-testid="product-image-prev"
                    onClick={() => go(-1)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 p-2.5 bg-black/50 backdrop-blur-sm text-zinc-300 border border-white/10 opacity-0 group-hover:opacity-100 hover:text-gold hover:border-gold/40 transition-all duration-300"
                    aria-label="Previous image"
                  >
                    <ChevronLeft size={18} strokeWidth={1.5} />
                  </button>
                  <button
                    data-testid="product-image-next"
                    onClick={() => go(1)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 bg-black/50 backdrop-blur-sm text-zinc-300 border border-white/10 opacity-0 group-hover:opacity-100 hover:text-gold hover:border-gold/40 transition-all duration-300"
                    aria-label="Next image"
                  >
                    <ChevronRight size={18} strokeWidth={1.5} />
                  </button>
                  <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-black/55 backdrop-blur-sm border border-white/10 text-[10px] tracking-[0.3em] text-zinc-300 font-mono">
                    {String(activeImg + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnail grid */}
            {n > 1 && (
              <div className="mt-4 grid grid-cols-5 gap-2.5" data-testid="product-thumbs">
                {images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={`product-thumb-${i}`}
                    onClick={() => setActiveImg(i)}
                    className={`relative aspect-square overflow-hidden border bg-[#0B0B0B] transition-all duration-300 ${
                      activeImg === i
                        ? "border-gold ring-1 ring-gold/40"
                        : "border-white/10 hover:border-white/40 opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img src={fileUrl(img)} alt="" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <FadeUp>
            <Overline>{product.partner || "Vincen Atelier"}</Overline>
            <h1 className="mt-4 font-serif text-4xl md:text-6xl font-light text-[#F5F5F5] leading-tight">
              {product.name}
            </h1>
            <p className="mt-4 text-xs tracking-[0.3em] uppercase text-zinc-500">
              {{ automobile: "Football \u00D7 Automobile", cinema: "Football \u00D7 Cinema", wilden: "Football \u00D7 Wilden", varsity: "Varsity", otter: "Pebble The Otter", originals: "Vincen Originals" }[product.collab_type] || "Vincen"} &mdash; {product.category}
            </p>
            <p data-testid="product-price" className="mt-8 font-serif text-3xl text-gold">
              {priceLabel(product.price)}
            </p>

            {product.description && (
              <p className="mt-8 text-[13px] text-zinc-400 leading-relaxed max-w-md whitespace-pre-line">
                {product.description}
              </p>
            )}

            <div className="mt-10">
              <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-4">Select Size</p>
              <div className="flex flex-wrap gap-3">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`size-${s}`}
                    onClick={() => setSize(s)}
                    className={`min-w-[3.5rem] px-4 py-3 border text-xs tracking-[0.2em] transition-colors duration-300 ${
                      size === s
                        ? "border-gold text-gold bg-gold/5"
                        : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-zinc-200"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <button
              data-testid="add-to-cart-button"
              onClick={handleAdd}
              className={`lux-shimmer mt-10 w-full md:w-auto px-14 py-4 text-xs tracking-[0.35em] uppercase font-semibold flex items-center justify-center gap-3 transition-colors duration-500 ${
                size
                  ? "bg-gold text-black hover:bg-gold-hover"
                  : "border border-white/20 text-zinc-400 hover:border-gold hover:text-gold"
              }`}
            >
              <ShoppingBag size={14} />
              Add to Selection
            </button>

            <div className="mt-12 pt-8 border-t border-white/10 grid grid-cols-3 gap-6">
              {["Strength", "Discovery", "Legacy"].map((t) => (
                <p key={t} className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 leading-relaxed">
                  {t}
                </p>
              ))}
            </div>
          </FadeUp>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && n > 0 && (
          <motion.div
            data-testid="product-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={() => setLightbox(false)}
            className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md flex items-center justify-center p-6 md:p-16"
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setLightbox(false)}
              className="absolute top-6 right-6 p-3 text-zinc-400 hover:text-gold border border-white/10 hover:border-gold/40 transition-colors duration-300"
              aria-label="Close"
            >
              <X size={18} strokeWidth={1.5} />
            </button>

            <motion.img
              key={activeImg}
              src={fileUrl(images[activeImg])}
              alt={product.name}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-[88vh] max-w-[92vw] object-contain"
            />

            {n > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); go(-1); }}
                  className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 p-3 text-zinc-300 hover:text-gold border border-white/10 hover:border-gold/40 transition-colors duration-300"
                  aria-label="Previous"
                >
                  <ChevronLeft size={22} strokeWidth={1.5} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); go(1); }}
                  className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 p-3 text-zinc-300 hover:text-gold border border-white/10 hover:border-gold/40 transition-colors duration-300"
                  aria-label="Next"
                >
                  <ChevronRight size={22} strokeWidth={1.5} />
                </button>
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 px-4 py-2 border border-white/10 text-[11px] tracking-[0.35em] text-zinc-300 font-mono">
                  {String(activeImg + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductDetail;
